import { useCallback, useMemo } from 'react';

import { useRequestAuthentication } from '@/packages/auth/useRequestAuthentication';
import { useHideOfferMutation } from '@/packages/query-state/shared/store/hidden-objects/v1/hide-offer-website/mutation';
import { useIsUserAuthenticatedQuery } from '@/packages/query-state/shared/store/monolith-python/cian-api/site/v1/get-user-data-by-request/query';

import { useCardApiContext } from '../../../context/CardApiContext';
import { useCardContext } from '../../../context/CardContext';
import { useListingAppContext } from '../../../context/ListingAppContext';
import { useCardOfferContext } from '../../../context/selectors';

export function useHideOffer(): VoidFunction {
  const { hideOfferMutationOptions } = useCardApiContext();
  const {
    dealType,
    offer: {
      id,
      publisher: { id: publisherId },
    },
  } = useCardContext();
  const {
    data: { url },
  } = useCardOfferContext();
  const ctx = useListingAppContext();
  const requestAuthentication = useRequestAuthentication(ctx, { alert: 'hide' });

  const { data: isUserAuthenticated } = useIsUserAuthenticatedQuery();

  const offerType = useMemo<'flat' | 'suburban' | 'commercial' | null>(() => {
    const parts = url.split('/');
    const dealTypeIndex = parts.findIndex(part => part === dealType);
    const offerTypeFromUrl = parts[dealTypeIndex + 1];

    if (['flat', 'suburban', 'commercial'].includes(offerTypeFromUrl)) {
      return offerTypeFromUrl as 'flat' | 'suburban' | 'commercial';
    }

    return null;
  }, [dealType, url]);

  const { mutate: hideOffer } = useHideOfferMutation(hideOfferMutationOptions);

  return useCallback<VoidFunction>(() => {
    if (isUserAuthenticated) {
      if (offerType !== null) {
        hideOffer({ dealType, offerType, offerId: id, publishedUserId: publisherId });
      }
    } else {
      requestAuthentication();
    }
  }, [dealType, hideOffer, id, isUserAuthenticated, offerType, publisherId, requestAuthentication]);
}
